import { useState, useEffect, useCallback } from 'react'
import {
  GitBranch,
  Plus,
  Trash2,
  Archive,
  Merge,
  Lock,
  Unlock,
  Loader2,
  Users,
  Clock,
  ChevronDown,
  ChevronRight,
  Terminal,
  ExternalLink,
} from 'lucide-react'
import {
  listAppSessions,
  createAppSession,
  openAppSession,
  lockAppSession,
  unlockAppSession,
  mergeAppSession,
  archiveAppSession,
  deleteAppSession,
  type AppSession,
} from '../lib/api'

interface SessionsPanelProps {
  appName: string
  onOpenSession?: (terminalUrl: string, chromeUrl: string) => void
}

export default function SessionsPanel({ appName, onOpenSession }: SessionsPanelProps) {
  const [sessions, setSessions] = useState<AppSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [expandedSession, setExpandedSession] = useState<string | null>(null)

  const loadSessions = useCallback(async () => {
    try {
      const result = await listAppSessions(appName, showArchived)
      setSessions(result.sessions)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }, [appName, showArchived])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  async function handleOpenSession(session: AppSession) {
    setActionLoading(`open-${session.id}`)
    try {
      const result = await openAppSession(appName, session.id)
      if (onOpenSession) {
        onOpenSession(result.terminal_url, result.chrome_url)
      } else {
        // Navigate to workspace
        window.location.href = result.terminal_url
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open session')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleLock(session: AppSession) {
    setActionLoading(`lock-${session.id}`)
    try {
      if (session.locked_by) {
        await unlockAppSession(appName, session.id)
      } else {
        await lockAppSession(appName, session.id)
      }
      await loadSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle lock')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleMerge(session: AppSession) {
    setActionLoading(`merge-${session.id}`)
    try {
      const result = await mergeAppSession(appName, session.id, false)
      if (result.success) {
        await loadSessions()
      } else {
        setError(result.message || 'Merge failed')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge session')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleArchive(session: AppSession) {
    if (!confirm(`Archive session "${session.name}"? This will preserve the Claude history but remove the worktree.`)) {
      return
    }
    setActionLoading(`archive-${session.id}`)
    try {
      await archiveAppSession(appName, session.id)
      await loadSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive session')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete(session: AppSession) {
    if (!confirm(`Delete session "${session.name}"? This will also delete the git branch.`)) {
      return
    }
    setActionLoading(`delete-${session.id}`)
    try {
      await deleteAppSession(appName, session.id)
      await loadSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete session')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const activeSessions = sessions.filter(s => s.status !== 'archived')
  const archivedSessions = sessions.filter(s => s.status === 'archived')

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          Sessions
        </h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Session
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-2">
        {activeSessions.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No sessions yet</p>
            <p className="text-sm">Create a session to start working</p>
          </div>
        ) : (
          activeSessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              appName={appName}
              isExpanded={expandedSession === session.id}
              onToggleExpand={() => setExpandedSession(
                expandedSession === session.id ? null : session.id
              )}
              actionLoading={actionLoading}
              onOpen={() => handleOpenSession(session)}
              onLock={() => handleLock(session)}
              onMerge={() => handleMerge(session)}
              onArchive={() => handleArchive(session)}
              onDelete={() => handleDelete(session)}
            />
          ))
        )}
      </div>

      {/* Archived Toggle */}
      {archivedSessions.length > 0 && (
        <div className="pt-4 border-t border-border">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showArchived ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <Archive className="w-4 h-4" />
            {archivedSessions.length} archived session{archivedSessions.length !== 1 ? 's' : ''}
          </button>
          {showArchived && (
            <div className="mt-2 space-y-2 pl-6">
              {archivedSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/50"
                >
                  <div>
                    <div className="font-medium text-muted-foreground">{session.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Branch: {session.branch}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(session)}
                    disabled={actionLoading === `delete-${session.id}`}
                    className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                    title="Delete permanently"
                  >
                    {actionLoading === `delete-${session.id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSessionModal
          appName={appName}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            loadSessions()
          }}
        />
      )}
    </div>
  )
}

interface SessionCardProps {
  session: AppSession
  appName: string
  isExpanded: boolean
  onToggleExpand: () => void
  actionLoading: string | null
  onOpen: () => void
  onLock: () => void
  onMerge: () => void
  onArchive: () => void
  onDelete: () => void
}

function SessionCard({
  session,
  appName,
  isExpanded,
  onToggleExpand,
  actionLoading,
  onOpen,
  onLock,
  onMerge,
  onArchive,
  onDelete,
}: SessionCardProps) {
  const isMain = session.name === 'main'
  const isLocked = !!session.locked_by
  const hasPresence = session.presence && session.presence.length > 0
  const branchPreviewUrl = !isMain ? `/a/branch/${session.name}/${appName}/` : null

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      {/* Main Row */}
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{session.name}</span>
            {isMain && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary">
                main
              </span>
            )}
            {isLocked && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500/10 text-yellow-500 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {session.locked_by_email?.split('@')[0]}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <GitBranch className="w-3 h-3" />
            {session.branch}
            {hasPresence && (
              <span className="flex items-center gap-1 text-green-500">
                <Users className="w-3 h-3" />
                {session.presence.length} online
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onOpen}
            disabled={actionLoading === `open-${session.id}`}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-50"
          >
            {actionLoading === `open-${session.id}` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Terminal className="w-4 h-4" />
            )}
            Open
          </button>
          {branchPreviewUrl && (
            <a
              href={branchPreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
              Preview
            </a>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border">
          <div className="grid grid-cols-2 gap-4 py-3 text-sm">
            <div>
              <span className="text-muted-foreground">Created</span>
              <div>{session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Last accessed</span>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {session.last_accessed_at
                  ? formatRelativeTime(session.last_accessed_at)
                  : 'Never'}
              </div>
            </div>
            {session.worktree_path && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Worktree</span>
                <div className="font-mono text-xs">{session.worktree_path}</div>
              </div>
            )}
            {branchPreviewUrl && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Branch Preview</span>
                <div className="font-mono text-xs flex items-center gap-2">
                  <a href={branchPreviewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                    {branchPreviewUrl}
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <button
              onClick={onLock}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs hover:bg-secondary transition-colors disabled:opacity-50"
              title={isLocked ? 'Unlock session' : 'Lock session'}
            >
              {isLocked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {isLocked ? 'Unlock' : 'Lock'}
            </button>

            {!isMain && (
              <>
                <button
                  onClick={onMerge}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-xs hover:bg-secondary transition-colors disabled:opacity-50"
                  title="Merge into main"
                >
                  <Merge className="w-3 h-3" />
                  Merge
                </button>

                <button
                  onClick={onArchive}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-xs hover:bg-secondary transition-colors disabled:opacity-50"
                  title="Archive session"
                >
                  <Archive className="w-3 h-3" />
                  Archive
                </button>

                <button
                  onClick={onDelete}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                  title="Delete session"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function CreateSessionModal({
  appName,
  onClose,
  onCreated,
}: {
  appName: string
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return

    setCreating(true)
    setError(null)

    try {
      await createAppSession(appName, name.trim().toLowerCase())
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session')
    } finally {
      setCreating(false)
    }
  }

  const isValidName = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(name)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-1">New Session</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Create a new git branch and worktree for feature development
        </p>

        <form onSubmit={handleCreate}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Session Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., fix-login-bug"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {name && !isValidName && (
              <p className="text-xs text-destructive mt-1">
                Use lowercase letters, numbers, and hyphens only. Must start and end with alphanumeric.
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim() || !isValidName}
              className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}
